import React, { FC, useMemo, useRef } from 'react'
import {
  ComboboxProvider,
  useComboboxStore,
  useStoreState,
} from '@ariakit/react'
import { useStoreId } from '@moonpig/web-core-stores'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import type {
  OnClearRecentSearches,
  OnRemindersVisible,
  OnRemoveRecentSearch,
  OnSuggestionsVisible,
  RecentSearchType,
  OnClear,
  OnFocus,
  OnSubmit,
  OnKeyUp,
  SuggestionType,
} from '../types'
import { NavigationalSuggestionType } from '../NavigationalSuggestions/NavigationalSuggestion'
import { DepartmentType, filterStoreAllowedDepartments } from '../departmentMap'
import { MAX_NAVIGATIONAL_SUGGESTIONS, MAX_SUGGESTIONS } from '../constants'
import { useLocaleText } from '../../locale'
import { BackButton } from '../BackButton'
import { SearchControls } from './SearchControls/SearchControls'
import { DepartmentTabs } from '../DepartmentTabs/DepartmentTabs'
import { useCachedSearchRows } from '../hooks/useCachedSearchRows'
import { Popover } from './Popover'
import { SectionedList } from './SectionedList'
import { Overlay } from './Overlay'
import type { Reminder } from '../Reminders/Reminder'

type Props = {
  searchTerm: string
  clearButtonAs: (({ value }: { value: string }) => React.ReactNode) | undefined
  onSubmit: OnSubmit
  isSearchOpen: boolean
  setIsSearchOpen: (open: boolean) => void
  activeDepartment: DepartmentType
  setActiveDepartment: (dept: DepartmentType) => void
  searchInput: React.RefObject<HTMLInputElement | null>
  popoverWidth?: number
  navigationalSuggestions: NavigationalSuggestionType[]
  navigationalHeaderText?: string
  suggestionsLoading?: boolean
  onSuggestionsVisible: OnSuggestionsVisible
  onRemindersVisible: OnRemindersVisible
  onRemoveRecentSearch: OnRemoveRecentSearch
  onClearRecentSearches: OnClearRecentSearches
  onClear: OnClear
  onFocus: OnFocus
  onKeyUp: OnKeyUp
  items: SuggestionType[]
  isMobile: boolean
  inspirations?: Array<{
    department: string
    searchTerm: string
    value: string
  }>
  recentSearches: RecentSearchType[]
  reminders: Reminder[]
  maxSuggestions?: number
  maxNavigationalSuggestions?: number
  searchDepartment: {
    name: string
    title: string
  }
}

export const SearchCombobox: FC<Props> = ({
  isSearchOpen,
  setIsSearchOpen,
  popoverWidth,
  searchInput,
  activeDepartment,
  setActiveDepartment,
  items,
  onSubmit,
  clearButtonAs,
  searchTerm,
  isMobile,
  onClear,
  onFocus,
  onKeyUp,
  navigationalSuggestions,
  recentSearches,
  inspirations,
  reminders,
  maxSuggestions,
  maxNavigationalSuggestions,
  onClearRecentSearches,
  onRemoveRecentSearch,
  onRemindersVisible,
  suggestionsLoading,
  searchDepartment,
}) => {
  const localiseText = useLocaleText()
  const storeId = useStoreId()
  const firstListItemlRef = useRef<HTMLDivElement>(null)
  const combobox = useComboboxStore({
    open: isSearchOpen,
    setOpen: setIsSearchOpen,
    defaultValue: searchTerm,
  })
  const inputValue = useStoreState(combobox, 'value')
  const isComboboxOpen = useStoreState(combobox, 'open')
  const activeId = useStoreState(combobox, 'activeId')

  const closeSearch = () => combobox.setOpen(false)
  const handleInputFocus = (searchOpen: boolean) => {
    combobox.setOpen(searchOpen)
    if (searchOpen) {
      onFocus(inputValue)
    }
  }

  const getSearchRows = useCachedSearchRows({
    departments: filterStoreAllowedDepartments(storeId),
    activeDepartment,
    items,
    navigationalSuggestions,
    recentSearches,
    inspirations: inspirations || /* istanbul ignore next */ [],
    reminders,
    localiseText,
    maxSuggestions:
      maxSuggestions ?? /* istanbul ignore next */ MAX_SUGGESTIONS,
    maxNavigationalSuggestions:
      maxNavigationalSuggestions ??
      /* istanbul ignore next */ MAX_NAVIGATIONAL_SUGGESTIONS,
  })

  const {
    sections: { departmentRows },
    flatList,
  } = getSearchRows(inputValue)

  const activeRow = useMemo(
    () => flatList.find(r => r.id === activeId),
    [flatList, activeId],
  )

  const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return
    if (!activeRow || activeRow.kind !== 'recent') return

    e.preventDefault()

    onRemoveRecentSearch({
      department:
        activeRow.item.department ?? /* istanbul ignore next */ 'all_cards',
      searchTerm: activeRow.item.searchTerm,
      value: activeRow.item.value,
    })
  }

  return (
    <ComboboxProvider store={combobox}>
      <BackButton
        onClick={closeSearch}
        aria-label={localiseText('search.close_search')}
        shouldDisplay={isComboboxOpen}
      />
      <SearchControls
        localiseText={localiseText}
        inputValue={inputValue}
        isOpen={isSearchOpen}
        searchInput={searchInput}
        onClear={() => {
          combobox.setValue('')
          onClear()
        }}
        clearButtonAs={clearButtonAs}
        activeDepartment={activeDepartment}
        handleInputFocus={handleInputFocus}
        closeSearch={closeSearch}
        items={items}
        onSubmit={onSubmit}
        isMobile={isMobile}
        onKeyUp={onKeyUp}
        onKeyDown={onInputKeyDown}
        searchDepartment={searchDepartment}
      />

      {isComboboxOpen && (
        <>
          <RemoveScrollBar />
          <Overlay setIsSearchOpen={setIsSearchOpen} />
          <Popover popoverWidth={popoverWidth}>
            <DepartmentTabs
              selectedDepartment={activeDepartment}
              tabs={departmentRows}
              storeId={storeId}
              setActiveDepartment={setActiveDepartment}
              searchInputRef={searchInput}
              firstListItemlRef={firstListItemlRef}
            />
            <SectionedList
              flatList={flatList}
              firstListItemlRef={firstListItemlRef}
              onSubmit={onSubmit}
              inputValue={inputValue}
              onClearRecentSearches={onClearRecentSearches}
              localiseText={localiseText}
              onRemoveRecentSearch={onRemoveRecentSearch}
              activeDepartment={activeDepartment}
              onRemindersVisible={onRemindersVisible}
              suggestionsLoading={suggestionsLoading && items.length === 0}
            />
          </Popover>
        </>
      )}
    </ComboboxProvider>
  )
}
